import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Phone, Linkedin, ArrowRight, Check } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { FIRM } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINES = ["Something", "considered", "is coming."];
const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({ y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.4 + i * 0.13 } }),
};

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email.");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { email, source: "coming-soon-notify" });
      setDone(true);
      toast.success("You're on the list — we'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#050A14] text-[#F2EFE9] overflow-hidden flex flex-col" data-testid="coming-soon">
      <div className="noise-overlay" />
      {/* animated accent orb */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
        className="pointer-events-none absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(217,90,65,0.28) 0%, rgba(217,90,65,0) 70%)" }}
      />
      <motion.div
        animate={{ y: [0, -25, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute bottom-[-10rem] left-[-8rem] w-[30rem] h-[30rem] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(11,19,43,0.9) 0%, rgba(5,10,20,0) 70%)" }}
      />

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 max-w-[1200px] w-full mx-auto px-6 lg:px-10 pt-8 flex items-center gap-3"
      >
        <LogoMark size={32} />
        <div className="flex flex-col leading-none">
          <span className="text-lg font-900 tracking-tightest">KAMBOJ</span>
          <span className="text-[10px] font-500 tracking-[0.35em] text-[#94A3B8] uppercase">Law Chambers</span>
        </div>
      </motion.header>

      {/* Main */}
      <main className="relative z-10 flex-1 flex items-center">
        <div className="max-w-[1200px] w-full mx-auto px-6 lg:px-10 py-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="text-sm font-500 tracking-[0.3em] uppercase text-[#D95A41] mb-8"
          >
            The Outsourced Legal Department · Gurugram
          </motion.p>

          <h1 className="text-[15vw] md:text-[10vw] lg:text-[8.5vw] font-900 leading-[0.9] tracking-tightest">
            {LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">
                  {line === "considered" ? <span className="text-[#D95A41]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-10 max-w-xl text-lg md:text-xl text-[#94A3B8] leading-relaxed"
          >
            A new home for Kamboj Law Chambers is on its way — board-level legal judgment, delivered as a
            Fractional General Counsel for growth-stage and mid-market businesses. Leave your email and we'll let you know the moment we're live.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-10 max-w-md"
          >
            {done ? (
              <div className="flex items-center gap-3 rounded-2xl border border-[#D95A41]/40 bg-[#0B132B]/50 px-6 py-5" data-testid="notify-success">
                <span className="w-10 h-10 rounded-full bg-[#D95A41] flex items-center justify-center shrink-0"><Check size={20} /></span>
                <p className="text-[#F2EFE9]">Thank you — you're on the list. We'll be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="notify-form">
                <input
                  type="email"
                  required
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="notify-email"
                  className="flex-1 bg-[#0B132B] border border-white/10 rounded-full px-6 py-4 text-[#F2EFE9] placeholder:text-[#94A3B8] focus:border-[#D95A41] outline-none transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="notify-submit"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#D95A41] text-[#F2EFE9] font-700 hover:bg-[#E6735A] transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {loading ? "Adding…" : <>Notify me <ArrowRight size={18} /></>}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative z-10 border-t border-white/10"
      >
        <div className="max-w-[1200px] w-full mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <a href={`mailto:${FIRM.email}`} data-testid="cs-email" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#F2EFE9] transition-colors">
              <Mail size={15} className="text-[#D95A41]" /> {FIRM.email}
            </a>
            <a href={`tel:${FIRM.phone.replace(/\s/g, "")}`} data-testid="cs-phone" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#F2EFE9] transition-colors">
              <Phone size={15} className="text-[#D95A41]" /> {FIRM.phone}
            </a>
            <a href={FIRM.linkedin} target="_blank" rel="noopener noreferrer" data-testid="cs-linkedin" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#F2EFE9] transition-colors">
              <Linkedin size={15} className="text-[#D95A41]" /> LinkedIn
            </a>
          </div>
          <p className="text-xs text-[#94A3B8]/60">© {new Date().getFullYear()} {FIRM.name}. All rights reserved.</p>
        </div>
      </motion.footer>
    </div>
  );
}
